import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { useCallback, useEffect, useState } from 'react';
import { getMeThunk, user } from '../../../account/api/accountSlice';
import { editEmail, patientState, verifyEmailCode } from '../../api/AccountSlice';

import { yupResolver } from '@hookform/resolvers/yup';
import { NewEmailVerificationSchema } from '../../schemas';
import VerificationInput from 'react-verification-input';
import ResendCodeButton from '../../../../components/common/ResendCodeButton';
import emailToParams from '../../../../utils/emailToParams';
import CustomModal from '../../../app/components/CustomModal';
import { searchUserThunk } from '../../../register/api/registerSlice';
import alert from '../../../../utils/alert';
import { useForm } from 'react-hook-form';
import { Form } from 'react-bootstrap';

type PhoneFormData = {
  email: string;
};

const NewEmailVerification = ({
  emailEditable,
  setEmailEditable,
}: {
  emailEditable: boolean;
  setEmailEditable: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { isLoading } = useAppSelector(patientState);

  const [step, setStep] = useState(0);
  const [verCode, setVerCode] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(true);
  const [checkingEmailAvailability, setCheckingEmailAvailability] = useState(false);
  const [emailChanged, setEmailChanged] = useState(false);
  const [submitEmailLoader, setSubmitEmailLoader] = useState<boolean>(false);

  const userInfo = useAppSelector(user);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    setValue,
  } = useForm<PhoneFormData>({
    mode: 'onChange',
    resolver: yupResolver(NewEmailVerificationSchema),
  });

  useEffect(() => {
    if (emailChanged) {
      setEmailChanged(false);
    }
  }, [emailChanged]);

  const onSubmit = async () => {
    if (step === 0) {
      try {
        await dispatch(editEmail(getValues('email'))).unwrap();
        setStep(step + 1);
      } catch (e) {
        alert.error(t('Something went wrong. Please try again.'));
      }
    } else if (step === 1) {
      const params = emailToParams(userInfo, getValues('email'));
      try {
        setSubmitEmailLoader(true);
        await dispatch(
          verifyEmailCode({ userAttributeKey: 'email', confirmationCode: verCode, params }),
        ).unwrap();
        await dispatch(getMeThunk()).unwrap();
        alert.success(t('Your email address has been updated successfully.'));
        setEmailEditable(false);
      } catch (e: any) {
        if (e.name === 'CodeMismatchException') {
          alert.error(t('Code is incorrect. Please enter a valid code.'));
        } else {
          alert.error(t('Something went wrong. Please try again.'));
        }
      } finally {
        setSubmitEmailLoader(false);
      }
    }
  };

  const onResendButtonSubmit = async () => {
    setIsResending(true);
    try {
      await dispatch(editEmail(getValues('email'))).unwrap();
      alert.success(t('Verification code send successfully!'));
    } catch (e) {
      alert.error(t('Something went wrong. Please try again.'));
    } finally {
      setIsResending(false);
      setVerCode('');
    }
  };

  const title = () => {
    switch (step) {
      case 0:
        return t('New email verification');
      case 1:
        return t('Check your email');
      default:
        return '';
    }
  };

  const helpText = () => {
    switch (step) {
      case 0:
        return t('Enter the new email where you will receive a verification code.');
      case 1:
        return (
          <>
            <p>
              {t('We sent a code to ')} <strong>{getValues('email')}</strong>
            </p>
            <p>{t('Enter the code you recieved in your email.')}</p>
          </>
        );
      default:
        return '';
    }
  };

  const confirmText = () => {
    switch (step) {
      case 0:
        return t('Authenticate');
      case 1:
        return t('Confirm');
      default:
        return '';
    }
  };

  const checkIfEmailAvailable = useCallback((searchResult: any, value: string, type: string) => {
    if (searchResult.entry && searchResult.entry.length > 0) {
      const item = searchResult.entry[0]?.resource?.telecom?.find(
        (item: { system: string; value: string }) => item.system === type,
      );
      setEmailAvailable(!(item && item.value.toLowerCase() === value.toLowerCase()));
    } else {
      setEmailAvailable(true);
    }
    setCheckingEmailAvailability(false);
  }, []);

  const searchEmail = useCallback(
    async (value: string, type: string) => {
      setCheckingEmailAvailability(true);
      try {
        const searchBody = { telecom: value, limit: 1 };
        const resultAction = await dispatch(searchUserThunk(searchBody));
        if (searchUserThunk.fulfilled.match(resultAction)) {
          checkIfEmailAvailable(resultAction.payload, value, type);
        } else {
          setEmailAvailable(true);
        }
      } catch (error) {
        setEmailAvailable(true);
      } finally {
        setCheckingEmailAvailability(false);
      }
    },
    [dispatch, checkIfEmailAvailable],
  );

  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const email = event.target.value;
      setValue('email', email, { shouldValidate: true });
      if (email && email.includes('@')) {
        searchEmail(email, 'email');
      } else {
        setEmailAvailable(true);
      }
      setEmailChanged(true);
    },
    [setValue, searchEmail],
  );

  return (
    <CustomModal
      show={emailEditable}
      title={title()}
      helpText={helpText()}
      confirmText={confirmText()}
      cancelText='Cancel'
      useFlexibleFooter={true}
      handleCancel={() => {
        setEmailEditable(false);
      }}
      handleConfirm={handleSubmit(onSubmit)}
      editPersonalDetails={
        step === 0 ? !isValid || !emailAvailable || checkingEmailAvailability : verCode.length !== 6
      }
      isSubmitting={isLoading || submitEmailLoader}
    >
      <div className='mt-3'>
        {step === 0 && (
          <Form>
            <Form.Group>
              <Form.Label>{t('New email')}</Form.Label>
              <Form.Control
                type='text'
                {...register('email', {
                  onChange: handleEmailChange,
                })}
              />
              {errors.email?.message && (
                <Form.Text className='text-danger'>{t(errors.email.message)}</Form.Text>
              )}
              {!checkingEmailAvailability && !emailAvailable && (
                <Form.Text className='text-danger'>
                  {t('This Email is already registered.')}
                </Form.Text>
              )}
            </Form.Group>
          </Form>
        )}
        {step === 1 && (
          <>
            <VerificationInput
              value={verCode}
              length={6}
              placeholder=''
              onChange={setVerCode}
              classNames={{
                character: 'otp-input',
                characterSelected: 'otp-input--selected',
                container: 'ps-1 pe-1 mb-4 w-100',
              }}
              validChars='0-9'
              inputProps={{ inputMode: 'numeric' }}
            />
            <p>{t("Didn't receive the email? Check your spam folder")}</p>
            <ResendCodeButton
              onClick={onResendButtonSubmit}
              label={t('Resend Code')}
              seconds={30}
              isLoading={isResending}
              minimal={true}
            />
          </>
        )}
      </div>
    </CustomModal>
  );
};

export default NewEmailVerification;
